import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  getAuth,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseInstances } from '../../config/firebase/firebase';
import { notifyNewUserSignup } from '../../utils/webhookService';
import logger from '../../utils/logger';
import { initializeDefaultSettings } from '../../services/userSettingsService';
import SignupBrandPanel from './SignupBrandPanel';
import SignupForm from './SignupForm';
import SignupVerificationStep from './SignupVerificationStep';
import {
  emptySignupErrors,
  validateSignupField,
  validateSignupForm,
} from './signupValidation';
import { buildEmailVerificationActionCodeSettings } from './auth/authActionHelpers';
import styles from './LoginEnhanced.module.css';

const initialFormData = {
  email: '',
  password: '',
  confirmPassword: '',
  fullName: '',
  company: '',
  jobTitle: '',
  userType: 'General',
  useCase: '',
  referralSource: '',
  country: '',
  phoneNumber: '',
  selectedRole: 'analyst',
  adminFallbackRole: '',
  termsAccepted: false,
  privacyAccepted: false,
  marketingConsent: false
};

const signupErrorMessage = (error) => {
  if (error.code?.startsWith?.('functions/')) {
    return 'Account created, but invite finalization needs support. We saved the session for remediation.';
  }
  if (error.code === 'auth/email-already-in-use') {
    return 'This email is already registered. Please sign in instead.';
  }
  if (error.code === 'auth/invalid-email') return 'Invalid email address';
  if (error.code === 'auth/weak-password') return 'Password is too weak. Please use a stronger password.';
  if (error.code === 'auth/operation-not-allowed') {
    return 'Email/password accounts are not enabled. Please contact support.';
  }
  if (error.code === 'auth/too-many-requests') return 'Too many signup attempts. Please try again later.';
  return 'Failed to create account. Please try again.';
};

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resendParam = searchParams.get('resend');

  const [step, setStep] = useState(resendParam === 'true' ? 2 : 1);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(emptySignupErrors);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [createdUserEmail, setCreatedUserEmail] = useState('');
  const [verificationNotice, setVerificationNotice] = useState('');

  useEffect(() => {
    const reservation = sessionStorage.getItem('signupInviteReservation');
    const passwordVerified = sessionStorage.getItem('signupPasswordVerified');
    if (!reservation && passwordVerified !== 'true') {
      navigate('/signup-access', { replace: true });
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    setError((prev) => ({ ...prev, [name]: '', general: '' }));
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    const fieldError = validateSignupField(name, fieldValue, formData);
    setError((prev) => ({ ...prev, [name]: fieldError }));
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError(emptySignupErrors());
    try {
      const { auth } = await getFirebaseInstances();
      const user = auth.currentUser;

      if (user && !user.emailVerified) {
        await sendEmailVerification(user, buildEmailVerificationActionCodeSettings(window.location.origin));
        setVerificationNotice('Verification email sent. Please check your inbox.');
      } else if (!user) {
        setError((prev) => ({ ...prev, general: 'Session expired. Please sign up again.' }));
        setStep(1);
      } else {
        setError((prev) => ({ ...prev, general: 'Your email is already verified! You can now log in.' }));
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      const message = error.code === 'auth/too-many-requests'
        ? 'Too many requests. Please wait a few minutes before trying again.'
        : 'Failed to resend verification email';
      setError((prev) => ({ ...prev, general: message }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateSignupForm(formData);
    const hasErrors = Object.entries(validationErrors)
      .some(([key, value]) => key !== 'general' && Boolean(value));

    if (hasErrors) {
      setError({ ...validationErrors, general: 'Please fix the errors above' });
      return;
    }

    setLoading(true);
    setError(emptySignupErrors());

    try {
      const reservation = JSON.parse(sessionStorage.getItem('signupInviteReservation') || 'null');
      const legacyPasswordVerified = sessionStorage.getItem('signupPasswordVerified') === 'true';
      if (!legacyPasswordVerified && (!reservation?.inviteId || !reservation?.reservationId)) {
        setError((prev) => ({ ...prev, general: 'Access code reservation expired. Please enter your code again.' }));
        navigate('/signup-access', { replace: true });
        return;
      }

      const { app } = await getFirebaseInstances();
      const currentAuth = getAuth(app);
      const userCredential = await createUserWithEmailAndPassword(
        currentAuth,
        formData.email,
        formData.password
      );

      logger.debug('User account created:', userCredential.user.uid);

      const profileData = {
        displayName: formData.fullName,
        fullName: formData.fullName,
        company: formData.company,
        jobTitle: formData.jobTitle,
        userType: 'General',
        useCase: formData.useCase,
        referralSource: formData.referralSource,
        country: formData.country,
        phoneNumber: formData.phoneNumber,
        marketingConsent: formData.marketingConsent,
        termsAccepted: formData.termsAccepted,
        privacyAccepted: formData.privacyAccepted,
        profileCompleted: true
      };

      const functions = getFunctions(app);
      const consumeInvite = httpsCallable(functions, 'consumeSignupInvite');
      const requestId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);

      if (legacyPasswordVerified) {
        await initializeDefaultSettings(userCredential.user.uid, userCredential.user.email, profileData);
      } else {
        try {
          await consumeInvite({
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            inviteId: reservation.inviteId,
            reservationId: reservation.reservationId,
            requestId,
            profileData,
            governanceSelection: {
              selectedRole: 'analyst',
            },
          });
        } catch (finalizationError) {
          console.error('Invite finalization failed after Auth creation:', finalizationError);
          sessionStorage.setItem('signupFinalizationRecovery', JSON.stringify({
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            inviteId: reservation.inviteId,
            reservationId: reservation.reservationId,
            requestId,
            selectedRole: 'analyst',
          }));
          navigate('/signup-recovery', { replace: true });
          return;
        }
      }

      await sendEmailVerification(
        userCredential.user,
        buildEmailVerificationActionCodeSettings(window.location.origin)
      );
      logger.debug('Verification email sent to:', userCredential.user.email);
      await notifyNewUserSignup(userCredential.user, 'email', profileData);

      setCreatedUserEmail(userCredential.user.email);
      sessionStorage.removeItem('signupInviteReservation');
      sessionStorage.removeItem('signupPasswordVerified');
      sessionStorage.removeItem('signupFinalizationRecovery');
      setStep(2);
    } catch (err) {
      console.error('Signup error:', err);
      setError((prev) => ({ ...prev, general: signupErrorMessage(err) }));
    } finally {
      setLoading(false);
    }
  };

  if (step === 2) {
    return (
      <div className={styles.splitLayout}>
        <SignupBrandPanel styles={styles} />
        <SignupVerificationStep
          styles={styles}
          email={createdUserEmail || formData.email}
          errorMessage={error.general}
          verificationNotice={verificationNotice}
          loading={loading}
          onResendVerification={handleResendVerification}
          onBackToLogin={() => navigate('/login')}
        />
      </div>
    );
  }

  return (
    <div className={styles.splitLayout}>
      <SignupBrandPanel styles={styles} showTrustDetails />
      <SignupForm
        styles={styles}
        formData={formData}
        error={error}
        loading={loading}
        showPassword={showPassword}
        showConfirmPassword={showConfirmPassword}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        onTogglePassword={() => setShowPassword(v => !v)}
        onToggleConfirmPassword={() => setShowConfirmPassword(v => !v)}
      />
    </div>
  );
};

export default Signup;
